import { useMemo } from "react";
import { PageHeader } from "@/shared/ui/PageHeader";
import { isWebMode } from "@/shared/api";
import { usePermessi } from "@/shared/auth/permessi";
import { useTheme } from "@/shared/useTheme";
import { AccessiPortali } from "@/features/fatturazione/components/AccessiPortali";
import { AllServiceSettingsCard } from "./AllServiceSettingsCard";
import { ChiaviSalvate, ConnectionsList } from "./ConnectionsList";
import { CosmaSettingsCard } from "./CosmaSettingsCard";
import { CodaLavori, Pulizia, SistemaInfo } from "./Diagnostica";
import { IntervalsList, STATO_LANE } from "./IntervalsList";
import { LogViewer } from "./LogPanel";
import { MailAccountsCard } from "./MailAccountsCard";
import { MfaCard } from "./MfaCard";
import { PasswordCard } from "./PasswordCard";
import { UsersCard } from "./UsersCard";
import {
  type CredentialMeta,
  type LogTail,
  type SystemInfo,
  useResource,
  useSyncDashboard,
} from "./api";
import {
  CONNESSIONI,
  type HaltedLane,
  type Lettura,
  STATI_PROBLEMA,
  leggiConnessione,
  nomeLane,
  quandoBreve,
} from "./automatismi";
import { Foglio, GiornataAutomatismi, Indice, Pillola, Sezione, useSezioneVisibile } from "./parti";
import { useAutomatismi } from "./useAutomatismi";

// Impostazioni rifatta da zero il 28/10/2026 (Davide: «rifai completamente
// istruzioni e impostazioni, devono avere tutte le cose necessarie»).
// L'ordine segue la domanda che porta qui: prima «funziona tutto?», poi gli
// interruttori, poi le connessioni, l'accesso, l'aspetto e la parte tecnica.

const SEZIONI = [
  { id: "stato", nome: "Stato" },
  { id: "automatismi", nome: "Automatismi" },
  { id: "connessioni", nome: "Connessioni" },
  { id: "accesso", nome: "Accesso e utenti" },
  { id: "aspetto", nome: "Aspetto" },
  { id: "diagnostica", nome: "Diagnostica" },
];

const SYSTEM_VUOTO: SystemInfo = {
  app_name: "Order Manager",
  version: "",
  env: "",
  platform: "",
  python_version: "",
  data_dir: "",
  log_dir: "",
  poleepo_base_url: "",
};

/** Ruoli del login (auth/permessi): detti come li direbbe Davide. */
const NOMI_RUOLO: Record<string, string> = {
  god: "Amministratore",
  admin: "Operativo",
  amministrazione: "Amministrazione",
};

function Segnale({ n }: { n: number }) {
  if (n === 0) return null;
  return (
    <span className="min-w-[1.25rem] rounded-full bg-red px-1.5 text-center text-[11px] font-bold tabular-nums text-white">
      {n}
    </span>
  );
}

export function ImpostazioniPage() {
  const { isGod, utente } = usePermessi();
  const system = useResource<SystemInfo>("/api/system/info", SYSTEM_VUOTO);
  const credentials = useResource<CredentialMeta[]>("/api/credentials", []);
  const logTail = useResource<LogTail>("/api/system/log-tail?lines=200", {
    file: "",
    lines: [],
    truncated: false,
  });
  const sync = useSyncDashboard();
  const auto = useAutomatismi();
  const [attivo, setAttivo] = useSezioneVisibile(
    SEZIONI.map((s) => s.id),
    "stato",
  );

  const problemi = useMemo(
    () =>
      auto.intervals
        .map((i) => ({ module: i.module, l: auto.letture.get(i.module) }))
        .filter((x): x is { module: string; l: Lettura } => !!x.l && STATI_PROBLEMA.has(x.l.stato)),
    [auto.intervals, auto.letture],
  );
  const connessioniKo = useMemo(
    () => CONNESSIONI.filter((c) => leggiConnessione(c, auto.letture).stato === "errore").length,
    [auto.letture],
  );
  // Fermi che non sono di una lane sola: il worker «other» o lo scheduler.
  // Con uno di questi fermo, NIENTE parte: va detto in cima, a parte.
  const fermiGenerali = auto.halted.filter(
    (h) => !auto.intervals.some((i) => h.lane === `sync worker[${i.module}]`),
  );

  const voci = SEZIONI.map((s) => ({
    ...s,
    segnale:
      s.id === "stato" ? (
        <Segnale n={problemi.length + fermiGenerali.length} />
      ) : s.id === "automatismi" ? (
        <Segnale n={problemi.length} />
      ) : s.id === "connessioni" ? (
        <Segnale n={connessioniKo} />
      ) : undefined,
  }));

  return (
    <main className="flex-1 overflow-y-auto px-7 pb-24 pt-[22px] scroll-paper max-md:px-3 max-md:pb-10 max-md:pt-3">
      <PageHeader
        title="Impostazioni"
        subtitle="Come gira OM: automatismi, connessioni, accessi e diagnostica."
        meta={
          <>
            Versione
            <br />
            <strong className="text-[12px] tabular-nums text-ink">
              {system.value.version || "—"}
            </strong>
          </>
        }
      />

      <div className="mx-auto grid max-w-[1280px] grid-cols-[minmax(0,1fr)] gap-8 max-md:gap-4 md:[grid-template-columns:184px_minmax(0,1fr)]">
        <Indice
          voci={voci}
          attivo={attivo}
          onScegli={setAttivo}
          etichetta="Sezioni di Impostazioni"
        />

        <div className="min-w-0 space-y-12 max-md:space-y-9">
          <Sezione
            id="stato"
            titolo="Stato"
            sottotitolo="Se qualcosa non va, lo trovi qui per primo."
          >
            <StatoGenerale
              problemi={problemi}
              fermiGenerali={fermiGenerali}
              accesi={auto.intervals.filter((i) => i.enabled).length}
              totali={auto.intervals.length}
              stats={sync.stats}
              system={system.value}
              caricamento={auto.loading}
              errore={auto.error}
            />
            <Foglio className="mt-4">
              <h3 className="px-5 pt-4 text-[15px] font-bold text-ink max-md:px-4 max-md:text-[17px]">
                La giornata
              </h3>
              <GiornataAutomatismi
                accese={
                  auto.intervals.length > 0
                    ? new Set(auto.intervals.filter((i) => i.enabled).map((i) => i.module))
                    : undefined
                }
              />
            </Foglio>
          </Sezione>

          <Sezione
            id="automatismi"
            titolo="Automatismi"
            sottotitolo="Quello che OM fa da solo. «Agisce fuori» vuol dire che scrive a qualcuno o compra: prima di accenderlo ti chiede conferma."
          >
            <IntervalsList
              intervals={auto.intervals}
              letture={auto.letture}
              loading={auto.loading}
              loadError={auto.error}
              onRefresh={() => {
                void auto.refresh();
                void sync.refresh();
              }}
            />
          </Sezione>

          <Sezione
            id="connessioni"
            titolo="Connessioni"
            sottotitolo="I servizi con cui OM parla. Lo stato viene dall'ultimo giro degli automatismi che li usano."
          >
            <ConnectionsList letture={auto.letture} loading={auto.loading} error={auto.error} />

            <h3 className="mb-2 mt-8 px-1 text-[13px] font-bold text-ink max-md:text-[15px]">
              Account, accessi e file
            </h3>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <Foglio className="xl:col-span-2">
                <MailAccountsCard puoAggiungere={isGod} />
              </Foglio>
              {isGod && (
                <Foglio>
                  <AccessiPortali />
                </Foglio>
              )}
              <Foglio>
                <AllServiceSettingsCard onCredentialsChanged={() => void credentials.refresh()} />
              </Foglio>
              <Foglio className="xl:col-span-2">
                <CosmaSettingsCard onCredentialsChanged={() => void credentials.refresh()} />
              </Foglio>
              <Foglio className="xl:col-span-2">
                <h3 className="px-5 pt-4 text-[15px] font-bold text-ink max-md:px-4 max-md:text-[17px]">
                  Chiavi salvate da OM
                </h3>
                <p className="px-5 pb-2 text-[12.5px] text-ink-2 max-md:px-4 max-md:text-[14px] max-md:text-ink">
                  Solo nome e data: il valore non torna mai nella pagina.
                </p>
                <ChiaviSalvate
                  credentials={credentials.value}
                  loading={credentials.isLoading}
                  error={credentials.error}
                />
              </Foglio>
            </div>
          </Sezione>

          <Sezione
            id="accesso"
            titolo="Accesso e utenti"
            sottotitolo="La tua password, il secondo fattore e chi entra in OM."
          >
            <Foglio className="mb-4">
              <dl className="grid grid-cols-2 gap-x-6 px-5 py-4 text-[13px] max-md:px-4 max-md:text-[14.5px] md:grid-cols-4">
                {(
                  [
                    ["Sei entrato come", utente?.username],
                    ["Ruolo", utente ? (NOMI_RUOLO[utente.role] ?? utente.role) : undefined],
                    [
                      "Secondo fattore",
                      utente ? (utente.mfa_attiva ? "Attivo" : "Non attivo") : undefined,
                    ],
                    [
                      "Password",
                      utente
                        ? utente.password_scade_il
                          ? `scade il ${new Date(utente.password_scade_il).toLocaleDateString("it-IT")}`
                          : "non scade"
                        : undefined,
                    ],
                  ] as const
                ).map(([k, v]) => (
                  <div key={k} className="min-w-0 py-1">
                    <dt className="text-[12px] text-ink-2 max-md:text-[13px] max-md:text-ink">
                      {k}
                    </dt>
                    <dd className="truncate font-bold text-ink">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </Foglio>
            {isWebMode() && (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Password e secondo fattore: per TUTTI, non solo `god`. Chi
                    non puo' cambiarsi la password resta chiuso fuori il
                    giorno della scadenza annuale. */}
                <Foglio>
                  <PasswordCard />
                </Foglio>
                <Foglio>
                  <MfaCard />
                </Foglio>
                {/* Utenti: solo `god`. Il gate sta qui e non nella card, cosi'
                    la fetch di /api/auth/users (403 per gli altri) non parte. */}
                {isGod && (
                  <Foglio className="xl:col-span-2">
                    <UsersCard />
                  </Foglio>
                )}
              </div>
            )}
          </Sezione>

          <Sezione
            id="aspetto"
            titolo="Aspetto"
            sottotitolo="Vale per questo browser. Lo stesso tasto c'e' nella barra a sinistra."
          >
            <SceltaTema />
          </Sezione>

          <Sezione
            id="diagnostica"
            titolo="Diagnostica"
            sottotitolo="Per capire un guasto o mandare una segnalazione."
          >
            <div className="space-y-4">
              <Foglio>
                <CodaLavori
                  jobs={sync.jobs}
                  error={sync.error}
                  isRefreshing={sync.isRefreshing}
                  pendingIds={sync.pendingIds}
                  onRefresh={() => void sync.refresh()}
                  onRetry={(id) => void sync.retryJob(id)}
                  onCancel={(id) => void sync.cancelJob(id)}
                />
              </Foglio>
              <Foglio>
                <LogViewer log={logTail.value} loading={logTail.isLoading} error={logTail.error} />
              </Foglio>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Foglio>
                  <SistemaInfo system={system.value} error={system.error} />
                </Foglio>
                <Foglio>
                  <Pulizia />
                </Foglio>
              </div>
            </div>
          </Sezione>
        </div>
      </div>
    </main>
  );
}

function StatoGenerale({
  problemi,
  fermiGenerali,
  accesi,
  totali,
  stats,
  system,
  caricamento,
  errore,
}: {
  problemi: { module: string; l: Lettura }[];
  fermiGenerali: HaltedLane[];
  accesi: number;
  totali: number;
  stats: { queued: number; running: number; succeeded_24h: number; failed_24h: number };
  system: SystemInfo;
  caricamento: boolean;
  errore: string | null;
}) {
  const n = problemi.length + fermiGenerali.length;
  const fmt = (x: number) => x.toLocaleString("it-IT");
  const titolo = errore
    ? "Non riesco a leggere lo stato"
    : caricamento && totali === 0
      ? "Controllo…"
      : n === 0
        ? "Tutto funziona"
        : n === 1
          ? "Una cosa da guardare"
          : `${n} cose da guardare`;
  const tono = errore || n > 0 ? "bg-red" : caricamento ? "bg-ink-4" : "bg-green";
  const db = system.migration_status;

  return (
    <Foglio>
      <div className="flex items-center gap-3 px-5 pt-5 max-md:px-4 max-md:pt-4">
        <span aria-hidden="true" className={`h-3 w-3 shrink-0 rounded-full ${tono}`} />
        <p className="font-display text-[24px] font-bold leading-tight tracking-[-0.01em] text-ink max-md:text-[22px]">
          {titolo}
        </p>
      </div>
      {errore && <p className="px-5 pt-1 text-[13px] text-ink max-md:px-4">{errore}</p>}

      <dl className="flex flex-wrap gap-x-10 gap-y-3 px-5 pb-5 pt-3 text-[13px] max-md:grid max-md:grid-cols-2 max-md:gap-x-4 max-md:px-4 max-md:text-[14px]">
        <div>
          <dt className="text-ink-2 max-md:text-ink">Automatismi accesi</dt>
          <dd className="font-bold tabular-nums text-ink">
            {fmt(accesi)} su {fmt(totali)}
          </dd>
        </div>
        <div>
          <dt className="text-ink-2 max-md:text-ink">Lavori nelle ultime 24 ore</dt>
          <dd className="font-bold tabular-nums text-ink">
            {fmt(stats.succeeded_24h)} riusciti
            {stats.failed_24h > 0 && `, ${fmt(stats.failed_24h)} non riusciti`}
          </dd>
        </div>
        <div>
          <dt className="text-ink-2 max-md:text-ink">Adesso</dt>
          <dd className="font-bold tabular-nums text-ink">
            {fmt(stats.running)} in corso, {fmt(stats.queued)} in coda
          </dd>
        </div>
        <div>
          <dt className="text-ink-2 max-md:text-ink">Database</dt>
          <dd className="font-bold text-ink">
            {db === "ok"
              ? "Aggiornato"
              : db === "ahead"
                ? "Più nuovo dell'app"
                : db === "error"
                  ? "Aggiornamento non riuscito"
                  : "—"}
          </dd>
        </div>
      </dl>

      {n > 0 && (
        <ul className="border-t border-line">
          {fermiGenerali.map((h) => (
            <li
              key={h.lane}
              className="flex items-start gap-3 border-t border-line px-5 py-3 first:border-t-0 max-md:px-4"
            >
              <Pillola tono="errore">Fermo</Pillola>
              <div className="min-w-0 text-[13px] text-ink max-md:text-[14.5px]">
                <strong className="font-bold">Il motore degli automatismi si è fermato</strong> (
                {h.lane}): finché OM non si riavvia, queste lane non partono.
                <span className="block break-words text-ink-2">{h.reason}</span>
              </div>
            </li>
          ))}
          {problemi.map(({ module, l }) => {
            const s = STATO_LANE[l.stato];
            return (
              <li key={module} className="border-t border-line first:border-t-0">
                <a
                  href={`#lane-${module}`}
                  className="flex items-start gap-3 px-5 py-3 no-underline transition-colors hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink-4 max-md:px-4"
                >
                  <Pillola tono={s.tono}>{s.nome}</Pillola>
                  <span className="min-w-0 flex-1 text-[13px] text-ink max-md:text-[14.5px]">
                    <strong className="font-bold">{nomeLane(module)}</strong>
                    {l.quando && <span className="text-ink-2"> · {quandoBreve(l.quando)}</span>}
                    {l.motivo && (
                      <span className="mt-0.5 block truncate text-ink-2 max-md:whitespace-normal max-md:text-ink">
                        {l.motivo}
                      </span>
                    )}
                  </span>
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 self-center fill-none stroke-current stroke-2 text-ink-3"
                  >
                    <path d="m9 6 6 6-6 6" />
                  </svg>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </Foglio>
  );
}

function SceltaTema() {
  const { theme, setTheme } = useTheme();
  const scelte = [
    {
      id: "light" as const,
      nome: "Chiaro",
      carta: "#fbf9f5",
      foglio: "#ffffff",
      testo: "#1f1b16",
      riga: "#ece6d9",
    },
    {
      id: "dark" as const,
      nome: "Scuro",
      carta: "#15120e",
      foglio: "#1e1a15",
      testo: "#f2ece2",
      riga: "#302a23",
    },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Tema"
      className="grid max-w-[560px] grid-cols-2 gap-4 max-md:gap-3"
    >
      {scelte.map((s) => {
        const scelto = theme === s.id;
        return (
          <button
            key={s.id}
            type="button"
            role="radio"
            aria-checked={scelto}
            onClick={() => setTheme(s.id)}
            className={[
              "group rounded-lg border-2 bg-card p-2 text-left transition-[border-color,transform] duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 focus-visible:ring-offset-2",
              scelto ? "border-ink" : "border-line hover:border-line-2",
            ].join(" ")}
          >
            {/* Anteprima disegnata coi colori veri del tema. */}
            <div
              aria-hidden="true"
              className="flex h-[92px] gap-1.5 overflow-hidden rounded-md p-1.5"
              style={{ background: s.carta }}
            >
              <div className="w-4 rounded-sm" style={{ background: "#1f1b16" }} />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="h-2.5 w-1/2 rounded-sm" style={{ background: s.testo }} />
                <div
                  className="flex-1 rounded-sm border p-1.5"
                  style={{ background: s.foglio, borderColor: s.riga }}
                >
                  <div
                    className="mb-1 h-1.5 w-3/4 rounded-full"
                    style={{ background: s.testo, opacity: 0.85 }}
                  />
                  <div
                    className="mb-1 h-1.5 w-2/3 rounded-full"
                    style={{ background: s.testo, opacity: 0.45 }}
                  />
                  <div
                    className="h-1.5 w-1/2 rounded-full"
                    style={{ background: "rgb(201 100 66)" }}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between px-1.5 pb-0.5 pt-2">
              <span className="text-[14px] font-bold text-ink">{s.nome}</span>
              <span
                aria-hidden="true"
                className={[
                  "flex h-5 w-5 items-center justify-center rounded-full border-2",
                  scelto ? "border-ink bg-ink" : "border-line-2",
                ].join(" ")}
              >
                {scelto && <span className="h-1.5 w-1.5 rounded-full bg-card" />}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
